import React from "react";
import * as HeroIcons from "@heroicons/react/outline";
import { TitleHeading } from "../typography/TitleHeading";
import Keyword from "../typography/Keyword";
import Section from "../layout/Section";
import BodyText from "../typography/BodyText";
import { useAnimation } from "../../hooks/useAnimate";
import { Pattern } from "../Pattern";
import { twMerge } from "tailwind-merge";

interface FeaturesGrid6Props {
  tag: string;
  title: string;
  description?: string | React.ReactNode;
  theme?: "white" | "gray";
  elements: {
    icon: string;
    name: string;
    description: string | React.ReactNode;
  }[];
  inverse?: boolean;
  half?: boolean;
  noMargin?: "no-margin" | "top" | "bottom";
}

export const FeaturesGrid6: React.FunctionComponent<FeaturesGrid6Props> = ({
  tag,
  title,
  elements,
  description,
  theme,
  inverse,
  half = false,
  noMargin,
}) => {
  const { id, animate } = useAnimation();

  return (
    <div className="relative">
      <Section theme={theme} id={id} noMargin={noMargin}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 z-10">
          <div
            {...animate("fade-right")}
            className={twMerge(
              ` max-w-3xl lg:mb-12 lg:pr-12 xl:pr-24`,
              half ? "col-span-6" : "col-span-5",
              inverse && "order-3",
            )}
          >
            <TitleHeading theme="gray" tag={tag} title={title} />
            {description && <BodyText className="mt-4">{description}</BodyText>}
          </div>
          <div
            className={twMerge(
              " order-2 max-w-3xl",
              half ? "col-span-6" : "col-span-7",
            )}
          >
            {elements.map((el, i) => {
              const Icon = HeroIcons[el.icon];
              return (
                <div
                  {...animate("fade-right", 100 * i)}
                  key={el.name}
                  className="flex space-x-8"
                >
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-gray-900 dark:bg-gray-800 mt-2 mx-auto shrink-0 grow-0">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="grow">
                    <Keyword title={el.name} description={el.description} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>
      <Pattern
        type="square"
        className={`hidden lg:block absolute h-1/6 xl:h-1/3 w-1/3 bottom-0 left-0 text-gray-200 dark:text-gray-800 opacity-70`}
      ></Pattern>
    </div>
  );
};
